/**
 * Website hosting rules shared by the API.
 * The public CNAME target is configuration, never a hostname invented here.
 */

const SEGMENT = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
const MULTI_PART_SUFFIXES = ["co.uk", "org.uk", "com.au", "net.au", "co.nz", "com.my", "com.sg"];

export type CapabilityChoice = { email: boolean; sites: boolean };

/** Omitted flags keep today's email-only create. An explicit flag set must pick at least one. */
export function resolveDomainCapabilities(input: {
	email?: boolean | undefined;
	sites?: boolean | undefined;
}): { ok: true; value: CapabilityChoice } | { ok: false; message: string } {
	const specified = input.email !== undefined || input.sites !== undefined;
	const email = specified ? (input.email ?? false) : true;
	const sites = specified ? (input.sites ?? false) : false;
	if (!email && !sites) return { ok: false, message: "Choose email, sites, or both." };
	return { ok: true, value: { email, sites } };
}

/** A short label for a website domain, such as main or leadgen. */
export function validateSiteTagName(
	name: string,
): { ok: true; name: string; key: string } | { ok: false; message: string } {
	const value = name.trim().replace(/\s+/g, " ");
	if (value.length < 1 || value.length > 32) return { ok: false, message: "Use a short tag, such as main or leadgen." };
	if (!/^[A-Za-z0-9][A-Za-z0-9 _-]*$/.test(value)) {
		return { ok: false, message: "Use letters, numbers, spaces, or hyphens." };
	}
	return { ok: true, name: value, key: value.toLowerCase() };
}

/** A site path such as `/link` or `/abu`. It is not a whole-domain redirect. */
export function validateRedirectSourcePath(
	value: string,
): { ok: true; path: string } | { ok: false; message: string } {
	let path = value.trim().toLowerCase();
	if (path === "" || path === "/") return { ok: false, message: "Enter a path such as /link." };
	if (!path.startsWith("/")) path = `/${path}`;
	path = path.replace(/\/+$/, "");
	const parts = path.split("/").filter((part) => part.length > 0);
	if (parts.length === 0 || parts.length > 8) return { ok: false, message: "Enter a path such as /link." };
	const segments: string[] = [];
	for (const part of parts) {
		const segment = validatePathSegment(part);
		if (!segment.ok) return { ok: false, message: "Enter a path such as /link." };
		segments.push(segment.segment);
	}
	return { ok: true, path: `/${segments.join("/")}` };
}

export function validatePathSegment(
	segment: string,
): { ok: true; segment: string } | { ok: false; message: string } {
	if (segment !== segment.trim() || segment.includes("\0")) {
		return { ok: false, message: "Path segment contains invalid or unsafe characters." };
	}
	if (segment.length === 0) return { ok: false, message: "Path segment is required." };
	if (segment.length > 63) return { ok: false, message: "Path segment is too long." };
	if (segment.includes("/") || segment.includes("\\") || segment.includes("..")) {
		return { ok: false, message: "Path segment contains invalid or unsafe characters." };
	}
	if (!SEGMENT.test(segment)) {
		return { ok: false, message: "Use lowercase letters, numbers and hyphens." };
	}
	return { ok: true, segment };
}

export function isApexDomain(domain: string): boolean {
	const name = domain.toLowerCase();
	for (const suffix of MULTI_PART_SUFFIXES) {
		if (name.endsWith(`.${suffix}`)) {
			const head = name.slice(0, -(suffix.length + 1));
			return head.length > 0 && !head.includes(".");
		}
	}
	const labels = name.split(".");
	return labels.length === 2;
}

/**
 * The customer CNAMEs their own hostname. www is not the Sites connection.
 * A root name still needs the DNS provider to flatten or alias that CNAME.
 */
export function sitesCnameHost(domain: string): { host: string; apex: boolean } {
	return { host: domain, apex: isApexDomain(domain) };
}

const CNAME_TARGET = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/;

/**
 * A configured Sites target must already be a hostname.
 * An address is rejected so the product cannot instruct an A record.
 * Certificates stay explicit on the edge. On-demand issuance is not used:
 * asking a certificate authority for every requested name, without a rate
 * limit and a proof of control, would issue certificates for names the
 * customer does not hold.
 */
export function sitesCnameTarget(configured: string | null | undefined): string | null {
	if (!configured) return null;
	const value = configured.trim().toLowerCase().replace(/\.$/, "");
	if (!value || value.includes("/") || value.includes(":") || /^\d{1,3}(?:\.\d{1,3}){3}$/.test(value)) return null;
	if (!CNAME_TARGET.test(value)) return null;
	return value;
}

export type SitesCnameStatus = "unconfigured" | "pending" | "detected" | "incorrect";

export interface SitesCnamePreview {
	host: string;
	target: string | null;
	apex: boolean;
	recordType: "CNAME";
	status: SitesCnameStatus;
	/** True only when a DNS check saw this CNAME. A hosting row is not enough. */
	ready: boolean;
	observed: string[];
	message: string;
}

function normalizeDnsName(value: string): string {
	return value.trim().toLowerCase().replace(/\.$/, "");
}

/**
 * Preview and verification for one Sites CNAME.
 * Pass observations only after a resolver lookup. Omitting them never marks the name ready.
 */
export function sitesDnsInstruction(
	domain: string,
	target: string | null,
	observation?: { cnames?: readonly string[]; addresses?: readonly string[] },
): SitesCnamePreview {
	const { host, apex } = sitesCnameHost(domain);
	const accepted = sitesCnameTarget(target);
	const base = { host, target: accepted, apex, recordType: "CNAME" as const };
	const where = apex
		? " This is a root name. Cloudflare can flatten a CNAME at the root and keep MX records beside it. A provider that cannot flatten a root CNAME cannot connect the root name this way."
		: " The address in the browser stays this name.";
	if (!accepted) {
		return {
			...base,
			status: "unconfigured",
			ready: false,
			observed: [],
			message: "The Sites endpoint is not configured yet. Bafe will show the CNAME target here once it is set.",
		};
	}
	const instruction = `Add a CNAME for ${host} with target ${accepted}.${where} Mail records stay separate from this CNAME.`;
	if (!observation) {
		return { ...base, status: "pending", ready: false, observed: [], message: `${instruction} This record has not been checked yet.` };
	}
	const cnames = observation.cnames?.map(normalizeDnsName).filter((value) => value.length > 0) ?? [];
	const addresses = observation.addresses?.map((value) => value.trim()).filter((value) => value.length > 0) ?? [];
	if (cnames.includes(accepted)) {
		return {
			...base,
			status: "detected",
			ready: true,
			observed: cnames,
			message: `The CNAME for ${host} points at ${accepted}.`,
		};
	}
	if (cnames.length > 0) {
		return {
			...base,
			status: "incorrect",
			ready: false,
			observed: cnames,
			message: `The CNAME for ${host} points at ${cnames[0]}. Change it to ${accepted}.`,
		};
	}
	if (addresses.length > 0) {
		// Addresses are not returned. A flattened root CNAME and a plain
		// address record look the same in public DNS, so neither is readiness.
		const message = apex
			? `Public DNS for ${host} returns an address, not a CNAME. Add a CNAME for ${host} with target ${accepted}. Cloudflare can flatten a root CNAME and will still answer with addresses, so those addresses are not confirmation. A provider that cannot flatten a root CNAME cannot connect the root name this way.`
			: `This name has an address record. Add a CNAME for ${host} with target ${accepted}. An address record does not connect the website.`;
		return {
			...base,
			status: "incorrect",
			ready: false,
			observed: [],
			message,
		};
	}
	return {
		...base,
		status: "pending",
		ready: false,
		observed: [],
		message: `${instruction} It is not in place yet.`,
	};
}

const DOMAIN_HOST = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/;

/** An https URL with a domain name. Credentials and other schemes are refused. */
function httpsDestination(value: string): string | null {
	let url: URL;
	try {
		url = new URL(value);
	} catch {
		return null;
	}
	if (url.protocol !== "https:") return null;
	if (url.username !== "" || url.password !== "") return null;
	if (!DOMAIN_HOST.test(url.hostname.toLowerCase())) return null;
	return url.toString();
}

export function validateRedirectDestination(
	destination: string,
): { ok: true; destination: string } | { ok: false; message: string } {
	let value = destination.trim();
	if (value.length === 0 || value.includes("\0") || /\s/.test(value)) {
		return { ok: false, message: "Enter a destination." };
	}
	if (/^http:\/\//i.test(value)) value = `https://${value.slice(7)}`;
	if (value.startsWith("/") && !value.startsWith("//")) {
		if (value.includes("..") || !/^\/[A-Za-z0-9._~/-]*$/.test(value)) {
			return { ok: false, message: "That path is not a safe destination." };
		}
		const first = value.split("/")[1]?.toLowerCase() ?? "";
		if (["etc", "var", "usr", "opt", "tmp", "home", "root", "bin", "lib", "proc", "sys"].includes(first)) {
			return { ok: false, message: "That path is not a safe destination." };
		}
		return { ok: true, destination: value };
	}
	if (value.toLowerCase().startsWith("https://")) {
		const stored = httpsDestination(value);
		if (!stored) return { ok: false, message: "Enter a destination." };
		return { ok: true, destination: stored };
	}
	if (value.includes(":") || value.includes("/") || value.includes("\\") || value.includes("@")) {
		return { ok: false, message: "Enter a destination." };
	}
	const domain = value.toLowerCase();
	if (!DOMAIN_HOST.test(domain)) {
		return { ok: false, message: "Enter a destination." };
	}
	return { ok: true, destination: domain };
}

const CONTROL_PLANE_NAMES = new Set([
	"organisations",
	"users",
	"sessions",
	"memberships",
	"domains",
	"mailboxes",
	"aliases",
	"hosted_sites",
	"site_redirects",
	"site_databases",
	"workspace_sites",
	"bafe",
	"postgres",
	"template0",
	"template1",
]);

/** Cluster databases that must never be listed or deleted as customer databases. */
const PROTECTED_DATABASES = new Set(["bafe", "postgres", "template0", "template1"]);

const PROTECTED_ROLES = new Set([
	"bafe",
	"postgres",
	"template0",
	"template1",
	"bafe_hosting_admin",
	"bafe_customer_roles",
]);

const NAME_PATTERN = /^[a-z][a-z0-9_]{0,62}$/;

/**
 * Privileges shown on the database screen, in the reference order.
 * PostgreSQL enforces SELECT, INSERT, UPDATE, DELETE, REFERENCES, TRIGGER,
 * CREATE, and temporary-table access. The remaining names are recorded with
 * the grant.
 */
export const CUSTOMER_DATABASE_PRIVILEGES = [
	"SELECT",
	"INSERT",
	"UPDATE",
	"DELETE",
	"CREATE",
	"ALTER",
	"INDEX",
	"DROP",
	"CREATE TEMPORARY TABLES",
	"LOCK TABLES",
	"REFERENCES",
	"CREATE VIEW",
	"SHOW VIEW",
	"CREATE ROUTINE",
	"ALTER ROUTINE",
	"EXECUTE",
	"EVENT",
	"TRIGGER",
] as const;

export type CustomerDatabasePrivilege = (typeof CUSTOMER_DATABASE_PRIVILEGES)[number];

const PRIVILEGE_SET = new Set<string>(CUSTOMER_DATABASE_PRIVILEGES);

export function isProtectedCustomerDatabase(name: string): boolean {
	return PROTECTED_DATABASES.has(name.trim().toLowerCase());
}

/** A customer database name. It must not be a Bafe control-plane database or table. */
export function validateCustomerDatabaseName(name: string): { ok: true; name: string } | { ok: false; message: string } {
	const value = name.trim().toLowerCase();
	if (!NAME_PATTERN.test(value)) {
		return { ok: false, message: "Use a short name that starts with a letter." };
	}
	if (CONTROL_PLANE_NAMES.has(value)) {
		return { ok: false, message: "That name is reserved." };
	}
	return { ok: true, name: value };
}

/** A customer database role. It must not be a server administration role. */
export function validateCustomerRoleName(name: string): { ok: true; name: string } | { ok: false; message: string } {
	const value = name.trim().toLowerCase();
	if (value === "") return { ok: false, message: "Username is required." };
	if (!NAME_PATTERN.test(value) || value.startsWith("pg_") || PROTECTED_ROLES.has(value)) {
		return { ok: false, message: "Invalid username." };
	}
	return { ok: true, name: value };
}

/** The operator types the password twice. Bafe does not generate one. */
export function validateCustomerDatabasePassword(
	password: string,
	confirm: string,
): { ok: true } | { ok: false; message: string } {
	if (password === "" || confirm === "") return { ok: false, message: "Password is required." };
	if (password !== confirm) return { ok: false, message: "Passwords do not match." };
	if (password.includes("\0")) return { ok: false, message: "Invalid password." };
	if (password.length < 12) return { ok: false, message: "Password must be at least 12 characters." };
	if (password.length > 256) return { ok: false, message: "Password is too long." };
	return { ok: true };
}

export function normalizeCustomerPrivileges(
	privileges: readonly string[],
): { ok: true; privileges: CustomerDatabasePrivilege[] } | { ok: false; message: string } {
	if (privileges.length === 0) return { ok: false, message: "Privileges are required." };
	const chosen = new Set<CustomerDatabasePrivilege>();
	for (const item of privileges) {
		if (typeof item !== "string") return { ok: false, message: "Invalid privilege." };
		const canonical = item.trim().toUpperCase();
		if (!PRIVILEGE_SET.has(canonical)) return { ok: false, message: "Invalid privilege." };
		chosen.add(canonical as CustomerDatabasePrivilege);
	}
	if (chosen.size === 0) return { ok: false, message: "Privileges are required." };
	return { ok: true, privileges: CUSTOMER_DATABASE_PRIVILEGES.filter((item) => chosen.has(item)) };
}

/** Deletion requires the operator to type DELETE, with no extra characters. */
export function confirmCustomerDatabaseDelete(confirm: string): { ok: true } | { ok: false; message: string } {
	if (confirm !== "DELETE") return { ok: false, message: "Type DELETE to confirm." };
	return { ok: true };
}

/** www canonical redirects are a site setting, not a row the customer creates here. */
export function isWwwCanonical(sourceHost: string, destination: string): boolean {
	const source = sourceHost.toLowerCase();
	let target = destination.trim().toLowerCase();
	if (target.startsWith("https://")) target = target.slice("https://".length).split("/")[0] ?? "";
	if (source.startsWith("www.") && target === source.slice(4)) return true;
	if (!source.startsWith("www.") && target === `www.${source}`) return true;
	return false;
}
